import React, { useRef, useEffect, useState } from 'react';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import { OrbitControls, Text, Environment } from '@react-three/drei';
import * as THREE from 'three';

// Teacher character for questions
function QuestionTeacher({ position, isThinking = false }) {
  const meshRef = useRef();
  const headRef = useRef();

  useFrame((state) => {
    if (meshRef.current) {
      // Gentle floating animation
      meshRef.current.position.y = position[1] + Math.sin(state.clock.elapsedTime) * 0.1;
      
      // Thinking animation
      if (isThinking && headRef.current) {
        headRef.current.rotation.y = Math.sin(state.clock.elapsedTime * 2) * 0.1;
      }
    }
  });

  return (
    <group ref={meshRef} position={position}>
      {/* Teacher body */}
      <mesh position={[0, -0.5, 0]}>
        <cylinderGeometry args={[0.3, 0.4, 1.2, 8]} />
        <meshStandardMaterial color="#4A90E2" />
      </mesh>
      
      {/* Teacher head */}
      <mesh ref={headRef} position={[0, 0.3, 0]}>
        <sphereGeometry args={[0.25, 16, 16]} />
        <meshStandardMaterial color="#FDBCB4" />
      </mesh>
      
      {/* Teacher eyes */}
      <mesh position={[-0.08, 0.35, 0.2]}>
        <sphereGeometry args={[0.03, 8, 8]} />
        <meshStandardMaterial color="#000000" />
      </mesh>
      <mesh position={[0.08, 0.35, 0.2]}>
        <sphereGeometry args={[0.03, 8, 8]} />
        <meshStandardMaterial color="#000000" />
      </mesh>
      
      {/* Teacher arms */}
      <mesh position={[-0.4, 0, 0]} rotation={[0, 0, Math.PI / 6]}>
        <cylinderGeometry args={[0.05, 0.05, 0.6, 8]} />
        <meshStandardMaterial color="#FDBCB4" />
      </mesh>
      <mesh position={[0.4, 0, 0]} rotation={[0, 0, -Math.PI / 6]}>
        <cylinderGeometry args={[0.05, 0.05, 0.6, 8]} />
        <meshStandardMaterial color="#FDBCB4" />
      </mesh>
      
      {/* Thinking indicator */}
      {isThinking && (
        <mesh position={[0, 0.8, 0]}>
          <sphereGeometry args={[0.1, 8, 8]} />
          <meshStandardMaterial color="#FFD700" transparent opacity={0.7} />
        </mesh>
      )}
    </group>
  );
}

// Question bubble component
function QuestionBubble({ text, position, questionNumber = 1 }) {
  const bubbleRef = useRef();

  useFrame((state) => {
    if (bubbleRef.current) {
      bubbleRef.current.position.y = position[1] + Math.sin(state.clock.elapsedTime * 1.5) * 0.05;
    }
  });

  return (
    <group ref={bubbleRef} position={position}>
      {/* Bubble background */}
      <mesh>
        <sphereGeometry args={[1.2, 16, 16]} />
        <meshStandardMaterial 
          color="#4A90E2" 
          transparent 
          opacity={0.8} 
        />
      </mesh>
      
      {/* Question number */}
      <Text
        position={[-0.8, 0.3, 0.1]}
        fontSize={0.3}
        color="#FFD700"
        anchorX="center"
        anchorY="middle"
        font="/fonts/inter.woff"
      >
        Q{questionNumber}
      </Text>
      
      {/* Question text */}
      <Text
        position={[0, 0, 0.1]}
        fontSize={0.15}
        color="#ffffff"
        anchorX="center"
        anchorY="middle"
        maxWidth={2}
        font="/fonts/inter.woff"
      >
        {text}
      </Text>
    </group>
  );
}

// Answer options component
function AnswerOptions({ options, position }) {
  const optionsRef = useRef();

  useFrame((state) => {
    if (optionsRef.current) {
      optionsRef.current.rotation.y = Math.sin(state.clock.elapsedTime * 0.5) * 0.1;
    }
  });

  return (
    <group ref={optionsRef} position={position}>
      {options.map((option, index) => (
        <group key={index} position={[
          Math.cos((index / options.length) * Math.PI * 2) * 1.5,
          0,
          Math.sin((index / options.length) * Math.PI * 2) * 1.5
        ]}>
          {/* Option bubble */}
          <mesh>
            <sphereGeometry args={[0.4, 16, 16]} />
            <meshStandardMaterial 
              color={index === 0 ? "#90E24A" : "#E24A90"} 
              transparent 
              opacity={0.7} 
            />
          </mesh>
          
          {/* Option letter */}
          <Text
            position={[0, 0, 0.1]}
            fontSize={0.2}
            color="#ffffff"
            anchorX="center"
            anchorY="middle"
            font="/fonts/inter.woff"
          >
            {String.fromCharCode(65 + index)}
          </Text>
          
          {/* Option text */}
          <Text
            position={[0, -0.2, 0.1]}
            fontSize={0.1}
            color="#ffffff"
            anchorX="center"
            anchorY="middle"
            maxWidth={0.8}
            font="/fonts/inter.woff"
          >
            {option}
          </Text>
        </group>
      ))}
    </group>
  );
}

// Assessment environment
function AssessmentRoom() {
  return (
    <>
      {/* Floor */}
      <mesh position={[0, -2, 0]} rotation={[-Math.PI / 2, 0, 0]}>
        <planeGeometry args={[20, 20]} />
        <meshStandardMaterial color="#2C3E50" />
      </mesh>
      
      {/* Walls */}
      <mesh position={[0, 2, -10]} rotation={[0, 0, 0]}>
        <planeGeometry args={[20, 8]} />
        <meshStandardMaterial color="#34495E" />
      </mesh>
      
      {/* Assessment board */}
      <mesh position={[0, 1, -9.9]} rotation={[0, 0, 0]}>
        <planeGeometry args={[8, 4]} />
        <meshStandardMaterial color="#1A252F" />
      </mesh>
      
      {/* Assessment title */}
      <Text
        position={[0, 2, -9.8]}
        fontSize={0.4}
        color="#FFD700"
        anchorX="center"
        anchorY="middle"
        font="/fonts/inter.woff"
      >
        Assessment Questions
      </Text>
      
      {/* Floating question marks */}
      {Array.from({ length: 5 }, (_, i) => (
        <Text
          key={i}
          position={[
            -6 + i * 3,
            0.5 + Math.sin(i) * 0.5,
            -9.8
          ]}
          fontSize={0.3}
          color="#4A90E2"
          anchorX="center"
          anchorY="middle"
          font="/fonts/inter.woff"
        >
          ?
        </Text>
      ))}
    </>
  );
}

// Main questions scene
function QuestionsScene3D({ questionsContent, currentQuestion = 0 }) {
  const { camera } = useThree();

  useEffect(() => {
    camera.position.set(0, 3, 8);
    camera.lookAt(0, 0, -2);
  }, [camera]);

  // Sample question data
  const sampleQuestion = {
    text: "What is the main concept discussed in the educational content?",
    options: [
      "Option A: First concept",
      "Option B: Second concept", 
      "Option C: Third concept",
      "Option D: Fourth concept"
    ]
  };

  return (
    <>
      {/* Environment */}
      <Environment preset="night" />
      
      {/* Lighting */}
      <ambientLight intensity={0.4} />
      <directionalLight position={[10, 10, 5]} intensity={1} />
      <pointLight position={[0, 4, 0]} intensity={0.8} color="#FFD700" />
      <pointLight position={[-5, 2, 0]} intensity={0.5} color="#4A90E2" />
      <pointLight position={[5, 2, 0]} intensity={0.5} color="#E24A90" />
      
      {/* Assessment room */}
      <AssessmentRoom />
      
      {/* Teacher */}
      <QuestionTeacher 
        position={[0, 0, -5]} 
        isThinking={true}
      />
      
      {/* Question bubble */}
      <QuestionBubble 
        text={sampleQuestion.text}
        position={[0, 2, -3]}
        questionNumber={currentQuestion + 1}
      />
      
      {/* Answer options */}
      <AnswerOptions 
        options={sampleQuestion.options}
        position={[0, 0, 0]}
      />
      
      {/* Floating assessment elements */}
      <Text
        position={[-4, 3, -2]}
        fontSize={0.3}
        color="#FFD700"
        anchorX="center"
        anchorY="middle"
        font="/fonts/inter.woff"
      >
        Assessment
      </Text>
      
      <Text
        position={[4, 3, -2]}
        fontSize={0.3}
        color="#4A90E2"
        anchorX="center"
        anchorY="middle"
        font="/fonts/inter.woff"
      >
        Questions
      </Text>
    </>
  );
}

// Error boundary component
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error('3D Scene Error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ 
          width: '100%', 
          height: '100%', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          fontSize: '18px',
          textAlign: 'center',
          padding: '20px'
        }}>
          <div>
            <h3>3D Scene Loading...</h3>
            <p>Please wait while the 3D environment initializes.</p>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Main component
const QuestionsScene = ({ questionsContent, currentQuestion = 0 }) => {
  return (
    <div style={{ width: '100%', height: '100%', position: 'relative' }}>
      <ErrorBoundary>
        <Canvas
          camera={{ position: [0, 3, 8], fov: 60 }}
          style={{ background: 'transparent' }}
          onCreated={({ gl }) => {
            gl.setClearColor(0x000000, 0);
          }}
        >
          <QuestionsScene3D 
            questionsContent={questionsContent} 
            currentQuestion={currentQuestion}
          />
          <OrbitControls
            enablePan={false}
            enableZoom={true}
            enableRotate={true}
            minDistance={5}
            maxDistance={15}
            minPolarAngle={Math.PI / 6}
            maxPolarAngle={Math.PI / 2}
          />
        </Canvas>
      </ErrorBoundary>
    </div>
  );
};

export default QuestionsScene;


